import React from 'react';
import { DhlAlert } from '@dhl-official/react-library';

import { DUIL_TAGS } from '@/constants/duilTags';
import {
    SnackbarContext,
    type SnackbarContextValue,
    type SnackbarOptions,
    type SnackbarType,
} from '@/context/SnackbarContext';

export const SnackbarProvider: React.FC<{
    children?: React.ReactNode;
    defaultSnackbarOptions?: Partial<SnackbarOptions>;
}> = ({ children, defaultSnackbarOptions }) => {
    type SnackbarItem = SnackbarOptions & {
        key: React.Key;
        type: Exclude<SnackbarType, 'open'>;
        alertProps?: Partial<React.ComponentProps<typeof DhlAlert>>;
    };

    const mergedDefaultOptionsRef = React.useRef({ ...(defaultSnackbarOptions ?? {}) });
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const nodeMapRef = React.useRef<Record<string, HTMLDivElement>>({});
    const timersRef = React.useRef<Record<string, number>>({});
    const configRef = React.useRef({ duration: Number(defaultSnackbarOptions?.duration ?? 10) });

    const normalizeType = React.useCallback((raw?: SnackbarType): Exclude<SnackbarType, 'open'> => {
        if (!raw || raw === 'open') return 'info';
        return raw;
    }, []);

    const toTitleText = React.useCallback(
        (opts: SnackbarOptions | undefined, fallback?: string) => {
            const base = opts?.title ?? fallback ?? '';
            return typeof base === 'string' ? base : String(base ?? '');
        },
        [],
    );

    const toBodyText = React.useCallback((opts: SnackbarOptions | undefined, fallback?: string) => {
        const base = opts?.description ?? opts?.message ?? fallback ?? '';
        return typeof base === 'string' ? base : String(base ?? '');
    }, []);

    const clearTimer = React.useCallback((key: React.Key) => {
        const id = timersRef.current[String(key)];
        if (id) {
            window.clearTimeout(id);
            delete timersRef.current[String(key)];
        }
    }, []);

    const closeSnackbar = React.useCallback(
        (key: React.Key) => {
            clearTimer(key);
            const k = String(key);
            const node = nodeMapRef.current[k];
            if (node?.parentNode) node.parentNode.removeChild(node);
            delete nodeMapRef.current[k];
        },
        [clearTimer],
    );

    const showSnackbar = React.useCallback(
        (optsOrMsg: SnackbarOptions | string, type?: Exclude<SnackbarType, 'open'>): React.Key => {
            const key =
                typeof optsOrMsg === 'string'
                    ? `snack-${Date.now()}`
                    : (optsOrMsg.key ?? `snack-${Date.now()}`);

            const next: SnackbarItem =
                typeof optsOrMsg === 'string'
                    ? {
                          ...mergedDefaultOptionsRef.current,
                          key,
                          type: normalizeType(type),
                          message: optsOrMsg,
                      }
                    : {
                          ...mergedDefaultOptionsRef.current,
                          ...optsOrMsg,
                          key,
                          type: normalizeType((optsOrMsg.type as SnackbarType | undefined) ?? type),
                          alertProps: (
                              optsOrMsg as SnackbarOptions & {
                                  alertProps?: SnackbarItem['alertProps'];
                              }
                          ).alertProps,
                      };

            const host = document.createElement('div');
            host.className = 'tw-w-full';
            host.setAttribute('data-snackbar-key', String(key));

            const alertEl = document.createElement(DUIL_TAGS.alert);
            alertEl.classList.add('snackbar-alert');
            alertEl.setAttribute('variant', 'toast');
            alertEl.setAttribute('position', 'top-right');
            alertEl.setAttribute('type', next.type);
            alertEl.setAttribute('body-text', toBodyText(next, ''));
            alertEl.setAttribute('show-close-button', '');

            if (next.title) {
                alertEl.setAttribute('title-text', toTitleText(next, ''));
            }

            host.appendChild(alertEl);
            containerRef.current?.prepend(host);
            nodeMapRef.current[String(key)] = host;

            clearTimer(key);
            const duration = Number(next.duration ?? configRef.current.duration);
            if (!Number.isNaN(duration) && duration > 0) {
                timersRef.current[String(key)] = window.setTimeout(() => {
                    closeSnackbar(key);
                    next.onClose?.();
                }, duration * 1000);
            }

            return key;
        },
        [clearTimer, closeSnackbar, normalizeType, toBodyText, toTitleText],
    );

    const destroySnackbars = React.useCallback(() => {
        Object.keys(timersRef.current).forEach((k) => window.clearTimeout(timersRef.current[k]));
        timersRef.current = {};
        Object.keys(nodeMapRef.current).forEach((k) => {
            const node = nodeMapRef.current[k];
            if (node?.parentNode) node.parentNode.removeChild(node);
        });
        nodeMapRef.current = {};
    }, []);

    const configFn = React.useCallback<SnackbarContextValue['config']>((conf) => {
        if (conf?.duration != null) configRef.current.duration = Number(conf.duration);
    }, []);

    React.useEffect(() => {
        return () => destroySnackbars();
    }, [destroySnackbars]);

    const value = React.useMemo<SnackbarContextValue>(
        () => ({
            showSnackbar,
            closeSnackbar,
            destroySnackbars,
            config: configFn,
        }),
        [showSnackbar, closeSnackbar, destroySnackbars, configFn],
    );

    return (
        <SnackbarContext.Provider value={value}>
            <div
                ref={containerRef}
                className="tw-fixed tw-right-6 tw-top-6 tw-z-[99999] tw-flex tw-max-w-[38rem] tw-flex-col tw-gap-2"
            />
            {children}
        </SnackbarContext.Provider>
    );
};
